import { FormControl, FormGroup, FormLabel, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { useMemo } from 'react';
import { Control, Controller, FieldErrors, Path, UseFormRegister, UseFormReturn } from 'react-hook-form';
import MultiCheckbox from 'src/components/MultiCheckBox';
import { VehicleAttributeOptionsError, VehicleAttributeOptionsState } from 'src/components/VehicleAttributeOptionsState';
import { Unit, UnitUsedForCalculation } from 'src/entities/licenseEntity';
import { DesignatedTruckFeature } from 'src/entities/orderEntity';
import { TruckEntity, TruckDetailFields } from 'src/entities/truckEntity';
import { VehicleAttributeCategory } from 'src/entities/VehicleAttributeValue.entity';
import { useVehicleAttributeOptions } from 'src/hooks/useVehicleAttributeOptions';

type Props<T extends TruckDetailFields> = {
  form: UseFormReturn<T>;
  control: Control<T>;
  register: UseFormRegister<T>;
  errors: FieldErrors<TruckEntity>
  unit: Unit;
  unitUsedForCalculation: UnitUsedForCalculation;
  features: DesignatedTruckFeature[];
  setFeatures: (features: DesignatedTruckFeature[]) => void;
  /**
   * 車両タイプと最大積載量を、この詳細設定ではなく呼び出し側が外に描くか。#329 P7
   *
   * 仮車両フォーム（PlanningTemporaryTruckInlineForm）は「トラック種類・台数」の欄で
   * 車両タイプを選び、その場で積載量も入れる形にしたので true を渡す。
   *
   * Why not ここに残して 2 か所に描く: どちらも register の非制御入力で同じ
   * RHF フィールドを指すため、片方に打っても他方の DOM 値が変わらず食い違う。
   * Why not 仮車両側で Details を使うのをやめる: 運行条件・車両仕様・コストは
   * 実車両マスタと同じ内容で、複製すると片方だけ直る。
   */
  truckTypeAndCapacitySetOutside?: boolean;
  /**
   * 車両条件の選択肢を引く会社。**編集する車両自身の会社**を渡す。#329
   *
   * 全社表示では一覧に他社の車両が混ざり、選択中の会社と車両の会社が食い違う。
   * ログイン会社で引くと、その車両の会社にしか無い値が選択肢に出ない（現在値は残るが、
   * 一度外すと選び直せず、他社にしか無い値を選ぶと保存が 400 になる）。
   *
   * Why not `TruckDetailFields` から読む: 会社は車両詳細ではなく車両そのものの属性で、
   * `company_id` は `TruckEntity` にしかない。仮車両（`TemporaryTruckEntity`）は会社を持たず、
   * 全社表示で他社のものを開く経路も無いので、渡さず従来どおりにする。
   */
  companyId?: number;
};

function Details<T extends TruckDetailFields>({
  form,
  control,
  register,
  errors,
  unit,
  unitUsedForCalculation,
  features,
  setFeatures,
  truckTypeAndCapacitySetOutside = false,
  companyId,
}: Props<T>) {
  const {
    optionsOf,
    isLoading: optionsAreLoading,
    isError: optionsFailed,
  } = useVehicleAttributeOptions(companyId);

  /**
   * 選択中の値を選択肢に足して描く。#329 P8
   *
   * 無効にした値・改名した値を持つ既存データを編集できなくしないため（設計書 §5-4）。
   * Select は現在値が選択肢に無いと表示が空欄になり、利用者には値が消えたように見える。
   */
  const selectOptions = (category: VehicleAttributeCategory, current: unknown) => (
    optionsOf(category, typeof current === 'string' ? [current] : [])
  );

  /**
   * 装置は MultiCheckbox に渡す。
   *
   * Why not selectOptions と同じく毎回作る: MultiCheckbox は options を依存に持つ effect で
   * 「options に無い値」を setValue で落とすので、参照が毎回変わると毎レンダー走る。
   * 現在値を含めているのは、含めないとその effect が既存データの値を黙って消すため（§5-4）。
   */
  const featureOptions = useMemo(
    () => optionsOf('features', features ?? []),
    [optionsOf, features],
  );

  return (
    <Stack gap={1}>
      <Stack direction="row" gap={1}>
        {!truckTypeAndCapacitySetOutside && (
        <TextField
          margin="dense"
          id="maximum_loading_capacity_kg"
          label={`最大積載量(${unit})`}
          type="text"
          fullWidth
          variant="standard"
          required
          {...register('maximum_loading_capacity_kg' as Path<T>)}
          error={'maximum_loading_capacity_kg' in errors}
          helperText={errors.maximum_loading_capacity_kg?.message}
          InputLabelProps={{ shrink: true }}
        />
        )}
        {
          !truckTypeAndCapacitySetOutside
          && unitUsedForCalculation === '重量と体積'
          && (
            <TextField
              margin="dense"
              id="loading_platform_volume_m3"
              label="最大積載量(m3)"
              type="text"
              fullWidth
              variant="standard"
              {...register('loading_platform_volume_m3' as Path<T>)}
              error={'loading_platform_volume_m3' in errors}
              helperText={errors.loading_platform_volume_m3?.message}
              InputLabelProps={{ shrink: true }}
            />
          )
        }
        {!truckTypeAndCapacitySetOutside && (
        <TextField
          margin="dense"
          id="maximum_loading_capacity_quantity"
          label="最大積載量(数量)"
          type="text"
          fullWidth
          variant="standard"
          {...register('maximum_loading_capacity_quantity' as Path<T>)}
          error={'maximum_loading_capacity_quantity' in errors}
          helperText={errors.maximum_loading_capacity_quantity?.message}
          InputLabelProps={{ shrink: true }}
        />
        )}
        <TextField
          margin="dense"
          id="max_num_pistons"
          label="最大回転数"
          type="text"
          fullWidth
          variant="standard"
          {...register('max_num_pistons' as Path<T>)}
          error={'max_num_pistons' in errors}
          helperText={errors.max_num_pistons?.message}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          id="max_num_locations_per_pistons"
          label="1回転あたりの最大納品先件数"
          type="text"
          fullWidth
          variant="standard"
          {...register('max_num_locations_per_pistons' as Path<T>)}
          error={'max_num_locations_per_pistons' in errors}
          helperText={errors.max_num_locations_per_pistons?.message}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          id="maximum_loading_rate"
          label="上限積載率"
          type="text"
          fullWidth
          variant="standard"
          {...register('maximum_loading_rate' as Path<T>)}
          error={'maximum_loading_rate' in errors}
          helperText={errors.maximum_loading_rate?.message}
          InputLabelProps={{ shrink: true }} // ← ここを追加
        />
        <TextField
          margin="dense"
          id="minimum_loading_rate"
          label="下限積載率"
          type="text"
          fullWidth
          variant="standard"
          {...register('minimum_loading_rate' as Path<T>)}
          error={'minimum_loading_rate' in errors}
          helperText={errors.minimum_loading_rate?.message}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      <Stack direction="row" gap={1}>
        {!truckTypeAndCapacitySetOutside && (
        <FormControl fullWidth>
          <InputLabel>車両タイプ</InputLabel>
          <Controller
            control={control}
            name={'klass' as Path<T>}
            render={({ field }) => (
              <Select
                id="klass"
                disabled={optionsAreLoading}
                label="車両タイプ"
                type="text"
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                {selectOptions('klass', field.value).map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            )}
          />
          <VehicleAttributeOptionsError isError={optionsFailed} />
        </FormControl>
        )}
        <FormControl fullWidth>
          <InputLabel>車種</InputLabel>
          <Controller
            control={control}
            name={'car_model' as Path<T>}
            render={({ field }) => (
              <Select
                id="car_model"
                disabled={optionsAreLoading}
                label="車種"
                type="text"
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                {selectOptions('car_model', field.value).map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            )}
          />
          <VehicleAttributeOptionsError isError={optionsFailed} />
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>床仕様</InputLabel>
          <Controller
            control={control}
            name={'floor_specification' as Path<T>}
            render={({ field }) => (
              <Select
                margin="dense"
                id="floor_specification"
                disabled={optionsAreLoading}
                label="床仕様"
                type="text"
                fullWidth
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                {selectOptions('floor_specification', field.value).map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            )}
          />
          <VehicleAttributeOptionsError isError={optionsFailed} />
        </FormControl>
      </Stack>
      <Stack direction="row" gap={1}>
        <FormControl fullWidth>
          <InputLabel>荷台高</InputLabel>
          <Controller
            control={control}
            name={'loading_platform_height' as Path<T>}
            render={({ field }) => (
              <Select
                id="loading_platform_height"
                disabled={optionsAreLoading}
                label="荷台高"
                type="text"
                fullWidth
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                {selectOptions('loading_platform_height', field.value).map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            )}
          />
          <VehicleAttributeOptionsError isError={optionsFailed} />
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>荷台幅</InputLabel>
          <Controller
            control={control}
            name={'loading_platform_width' as Path<T>}
            render={({ field }) => (
              <Select
                id="loading_platform_width"
                disabled={optionsAreLoading}
                label="荷台幅"
                type="text"
                fullWidth
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                {selectOptions('loading_platform_width', field.value).map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            )}
          />
          <VehicleAttributeOptionsError isError={optionsFailed} />
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>荷台長</InputLabel>
          <Controller
            control={control}
            name={'loading_platform_length' as Path<T>}
            render={({ field }) => (
              <Select
                margin="dense"
                id="loading_platform_length"
                disabled={optionsAreLoading}
                label="荷台長"
                type="text"
                fullWidth
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                {selectOptions('loading_platform_length', field.value).map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            )}
          />
          <VehicleAttributeOptionsError isError={optionsFailed} />
        </FormControl>
      </Stack>
      <Stack>
        <FormControl component="fieldset">
          <>
            <FormLabel component="legend">装置</FormLabel>
            <VehicleAttributeOptionsState isLoading={optionsAreLoading} isError={optionsFailed}>
              <FormGroup row>
                <MultiCheckbox<T, DesignatedTruckFeature>
                  form={form}
                  control={control}
                  name={'features' as Path<T>}
                  options={featureOptions}
                  defaultCheckedValues={features || []}
                  valueSetter={setFeatures}
                />
              </FormGroup>
            </VehicleAttributeOptionsState>
          </>
        </FormControl>
      </Stack>
      <Stack direction="row" gap={1}>
        <TextField
          margin="dense"
          id="truck_fuel_cost_yen_per_km"
          label="1kmあたりの燃料費"
          type="text"
          fullWidth
          variant="standard"
          {...register('truck_fuel_cost_yen_per_km' as Path<T>)}
          error={'truck_fuel_cost_yen_per_km' in errors}
          helperText={errors.truck_fuel_cost_yen_per_km?.message}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          id="truck_insurance_fee_yen_per_day"
          label="1日あたりの保険料"
          type="text"
          fullWidth
          variant="standard"
          {...register('truck_insurance_fee_yen_per_day' as Path<T>)}
          error={'truck_insurance_fee_yen_per_day' in errors}
          helperText={errors.truck_insurance_fee_yen_per_day?.message}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          id="truck_repair_cost_yen_per_day"
          label="1日あたりの修繕費"
          type="text"
          fullWidth
          variant="standard"
          {...register('truck_repair_cost_yen_per_day' as Path<T>)}
          error={'truck_repair_cost_yen_per_day' in errors}
          helperText={errors.truck_repair_cost_yen_per_day?.message}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          id="truck_expressway_fee_yen_per_shift"
          label="1日あたりの高速代"
          type="text"
          fullWidth
          variant="standard"
          {...register('truck_expressway_fee_yen_per_shift' as Path<T>)}
          error={'truck_expressway_fee_yen_per_shift' in errors}
          helperText={errors.truck_expressway_fee_yen_per_shift?.message}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
    </Stack>
  );
}

export default Details;
